import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  BookMarked,
  CheckCircle2,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  Home,
  Info,
  Loader2,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  addressSuggest,
  getCargoFromOrders,
  listAddresses,
  listLogisticsOrganizations,
  searchOrders,
  type OrderType,
  type StopCategory,
  type StopIn,
  type StopRole,
  type StopStatus,
  type StopView,
} from "@/api/logistics";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

// ── Справочники значений ──────────────────────────────────────────────────

const ROLE_OPTIONS: { value: StopRole; label: string; icon: React.ElementType }[] = [
  { value: "shipment", label: "Отгрузка", icon: ArrowUpFromLine },
  { value: "loading", label: "Загрузка", icon: ArrowDownToLine },
  { value: "internal", label: "Наша точка", icon: Home },
];
const ROLE_BADGE: Record<StopRole, { label: string; cls: string }> = {
  shipment: { label: "Отгрузка", cls: "bg-blue-100 text-blue-700" },
  loading: { label: "Загрузка", cls: "bg-amber-100 text-amber-700" },
  internal: { label: "Наша точка", cls: "bg-indigo-100 text-indigo-700" },
};

const CATEGORY_OPTIONS: { value: StopCategory; label: string }[] = [
  { value: "b2b", label: "B2B" },
  { value: "mp", label: "МП" },
  { value: "raw", label: "Сырьё" },
  { value: "general", label: "Общие" },
  { value: "product_dev", label: "Разработка продукта" },
  { value: "production", label: "Производство" },
  { value: "marketing", label: "Маркетинг" },
  { value: "office", label: "Офис" },
];
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((c) => [c.value, c.label]),
);

const STOP_STATUS_OPTIONS: { value: StopStatus; label: string }[] = [
  { value: "pending", label: "В работе" },
  { value: "done", label: "Выполнено" },
  { value: "returned", label: "Возврат" },
];
const STATUS_STYLE: Record<StopStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  done: "bg-emerald-100 text-emerald-700",
  returned: "bg-rose-100 text-rose-700",
};

const RUB = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

function fmtSlot(from: string | null, to: string | null): string | null {
  const f = (from || "").trim();
  const t = (to || "").trim();
  if (f && t) return `${f}–${t}`;
  if (f) return `с ${f}`;
  if (t) return `до ${t}`;
  return null;
}

const numOrNull = (s: string): number | null => {
  const v = s.trim();
  if (v === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isNaN(n) ? null : n;
};
const numStr = (v: number | null): string => (v == null ? "" : String(v));

const NUM_INPUT =
  "h-9 w-full rounded-md border border-input bg-background text-right text-sm tabular-nums ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// ── Форма точки ───────────────────────────────────────────────────────────

interface StopForm {
  address: string;
  label: string;
  comment: string;
  timeslot_from: string;
  timeslot_to: string;
  lat: string;
  lon: string;
  role: StopRole;
  category: StopCategory | "";
  organization_ref: string;
  organization_name: string;
  status: StopStatus;
  delivery_cost: string;
  waiting_fee: string;
  return_fee: string;
  weight_kg: string;
  pallets: string;
  items_count: string;
  boxes: string;
}

function formFromStop(s: StopView): StopForm {
  return {
    address: s.address ?? "",
    label: s.label ?? "",
    comment: s.comment ?? "",
    timeslot_from: s.timeslot_from ?? "",
    timeslot_to: s.timeslot_to ?? "",
    lat: s.lat ?? "",
    lon: s.lon ?? "",
    role: s.role,
    category: s.category ?? "",
    organization_ref: s.organization_ref ?? "",
    organization_name: s.organization_name ?? "",
    status: s.status,
    delivery_cost: numStr(s.delivery_cost),
    waiting_fee: numStr(s.waiting_fee),
    return_fee: numStr(s.return_fee),
    weight_kg: numStr(s.weight_kg),
    pallets: numStr(s.pallets),
    items_count: numStr(s.items_count),
    boxes: numStr(s.boxes),
  };
}

export interface StopCardProps {
  tripId: string;
  tripDate: string | null;
  stop: StopView;
  index: number;
  total: number;
  onMove: (index: number, dir: -1 | 1) => void;
  onSaveStop: (patch: StopIn) => Promise<boolean>;
  onDeleteStop: () => void;
  onAttach: (orderType: OrderType, orderRef: string) => void;
  onDetach: (orderId: string) => void;
}

export function StopCard(props: StopCardProps): React.ReactElement {
  const { tripId, tripDate, stop, index, total, onMove, onSaveStop, onDeleteStop, onAttach, onDetach } =
    props;

  // Baseline зависит ТОЛЬКО от редактируемых полей точки, НЕ от stop.orders:
  // иначе привязка заказа сбрасывала бы несохранённый ввод формы.
  const stopKey = JSON.stringify([
    stop.address, stop.label, stop.comment, stop.timeslot_from, stop.timeslot_to,
    stop.lat, stop.lon, stop.role, stop.category, stop.organization_ref,
    stop.organization_name, stop.status, stop.delivery_cost, stop.waiting_fee,
    stop.return_fee, stop.weight_kg, stop.pallets, stop.items_count, stop.boxes,
  ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const baseline = useMemo(() => formFromStop(stop), [stopKey]);
  const [form, setForm] = useState<StopForm>(baseline);
  const [mode, setMode] = useState<"summary" | "edit">(stop.address ? "summary" : "edit");
  const [addrMode, setAddrMode] = useState<"book" | "manual">("book");
  const [saving, setSaving] = useState(false);
  const [cargoLoading, setCargoLoading] = useState(false);

  useEffect(() => setForm(baseline), [baseline]);

  const set = <K extends keyof StopForm>(key: K, value: StopForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const isInternal = form.role === "internal";

  const finish = async () => {
    if (!dirty) {
      setMode("summary");
      return;
    }
    setSaving(true);
    const ok = await onSaveStop({
      address: form.address,
      label: form.label,
      comment: form.comment,
      timeslot_from: form.timeslot_from,
      timeslot_to: form.timeslot_to,
      lat: form.lat,
      lon: form.lon,
      role: form.role,
      category: form.category || null,
      organization_ref: isInternal ? null : form.organization_ref || null,
      organization_name: isInternal ? null : form.organization_name || null,
      status: form.status,
      delivery_cost: numOrNull(form.delivery_cost),
      waiting_fee: numOrNull(form.waiting_fee),
      return_fee: numOrNull(form.return_fee),
      weight_kg: numOrNull(form.weight_kg),
      pallets: numOrNull(form.pallets),
      items_count: numOrNull(form.items_count),
      boxes: numOrNull(form.boxes),
    });
    setSaving(false);
    if (ok) {
      toast.success(`Точка ${index + 1} сохранена`);
      setMode("summary");
    }
  };

  // Посчитать вес + шт (в базовых ед) из привязанных заказов и подставить в груз.
  const fillCargoFromOrders = async () => {
    setCargoLoading(true);
    try {
      const c = await getCargoFromOrders(tripId, stop.id);
      setForm((f) => ({
        ...f,
        weight_kg: c.weight_kg ? String(c.weight_kg) : "",
        items_count: c.items_count ? String(c.items_count) : "",
      }));
      const note = c.missing_weight.length ? ` (без веса: ${c.missing_weight.length} поз.)` : "";
      toast.success(`Из заказов: ${c.items_count} шт · ${c.weight_kg} кг${note}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Не удалось посчитать груз из заказов"));
    } finally {
      setCargoLoading(false);
    }
  };

  const slot = fmtSlot(stop.timeslot_from, stop.timeslot_to);
  const RoleIcon = ROLE_OPTIONS.find((r) => r.value === form.role)?.icon ?? Package;

  // ── Сводка (свёрнуто) ─────────────────────────────────────────────────
  if (mode === "summary") {
    return (
      <Card className={cn("p-3", stop.role === "internal" && "border-indigo-200 bg-indigo-50/40")}>
        <div className="flex items-center gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums">
            {index + 1}
          </span>
          <RoleIcon className="size-4 shrink-0 text-muted-foreground" />
          <button type="button" onClick={() => setMode("edit")} className="min-w-0 flex-1 text-left">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate font-medium">
                {stop.label || stop.address || "Адрес не указан"}
              </span>
              {stop.category && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {CATEGORY_LABEL[stop.category]}
                </span>
              )}
              {stop.organization_name && (
                <span className="truncate text-xs text-muted-foreground">· {stop.organization_name}</span>
              )}
              {slot && (
                <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                  <Clock className="size-3" /> {slot}
                </span>
              )}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {stop.address || "нажмите, чтобы заполнить"}
            </div>
          </button>
          {!isInternal && (
            <span
              className={cn(
                "flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium",
                stop.orders.length === 0 ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground",
              )}
              title="Привязано заказов"
            >
              <Package className="size-3.5" /> {stop.orders.length}
            </span>
          )}
          {stop.weight_kg != null && (
            <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{stop.weight_kg} кг</span>
          )}
          <span className={cn("whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium", STATUS_STYLE[stop.status])}>
            {STOP_STATUS_OPTIONS.find((o) => o.value === stop.status)?.label}
          </span>
          {stop.delivery_cost != null && (
            <span className="whitespace-nowrap text-sm font-medium tabular-nums">{RUB.format(stop.delivery_cost)} ₽</span>
          )}
          <RowActions index={index} total={total} onMove={onMove} onDelete={onDeleteStop} />
        </div>
      </Card>
    );
  }

  // ── Правка (вариант 1c: адрес-первый + сайдбар) ────────────────────────
  const badge = ROLE_BADGE[form.role];
  return (
    <Card
      className={cn(
        "border-2 border-primary/60 p-4",
        isInternal && "ring-2 ring-indigo-300",
      )}
    >
      {/* Шапка */}
      <div className="flex items-center gap-2">
        <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {index + 1}
        </span>
        <RoleIcon className="size-4 text-muted-foreground" />
        <span className="font-medium">Точка {index + 1}</span>
        <span className={cn("rounded px-1.5 py-0.5 text-xs font-medium", badge.cls)}>{badge.label}</span>
        <div className="ml-auto flex items-center gap-0.5 text-muted-foreground">
          <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="rounded p-1 hover:bg-accent disabled:opacity-40" aria-label="Выше">
            <ChevronUp className="size-4" />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="rounded p-1 hover:bg-accent disabled:opacity-40" aria-label="Ниже">
            <ChevronDown className="size-4" />
          </button>
          <button type="button" onClick={onDeleteStop} className="rounded p-1 hover:bg-destructive/10 hover:text-destructive" aria-label="Удалить точку">
            <Trash2 className="size-4" />
          </button>
          <button type="button" onClick={finish} disabled={saving} className="ml-1 inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent">
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : <ChevronsDownUp className="size-3.5" />}
            Свернуть
          </button>
        </div>
      </div>

      {/* Роль */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {ROLE_OPTIONS.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => set("role", r.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors",
              form.role === r.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background hover:bg-accent",
            )}
          >
            <r.icon className="size-3.5" /> {r.label}
          </button>
        ))}
      </div>

      {/* Две колонки */}
      <div className="mt-4 flex flex-col gap-4 lg:flex-row">
        {/* Левая колонка */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Адрес — герой */}
          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <MapPin className="size-4 text-muted-foreground" /> Адрес
              </span>
              <div className="flex rounded-md border border-input bg-background p-0.5">
                {(["book", "manual"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setAddrMode(m)}
                    className={cn(
                      "rounded px-2.5 py-0.5 text-xs transition-colors",
                      addrMode === m ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent",
                    )}
                  >
                    {m === "book" ? "Из книги" : "Вручную"}
                  </button>
                ))}
              </div>
            </div>
            {addrMode === "book" ? (
              <SavedAddressPicker
                kind={isInternal ? "own" : "client"}
                placeholder={isInternal ? "Выбрать нашу локацию…" : "Выбрать из книги адресов…"}
                current={form.label || form.address}
                onPick={(address, alias, lat, lon) =>
                  setForm((f) => ({ ...f, address, label: alias, lat: lat ?? "", lon: lon ?? "" }))
                }
              />
            ) : (
              <AddressField
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v, lat: "", lon: "" }))}
                onPick={(address, lat, lon) => setForm((f) => ({ ...f, address, lat: lat ?? "", lon: lon ?? "" }))}
              />
            )}
            {form.address && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md bg-background px-2.5 py-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />
                <span>
                  {form.address}
                  {form.lat && form.lon ? " · координаты определены" : " · без координат"}
                </span>
              </div>
            )}
          </div>

          {/* Организация + категория */}
          <div className="flex gap-3">
            {!isInternal && (
              <div className="min-w-0 flex-1">
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  Организация <span className="font-normal">(плательщик за логистику)</span>
                </div>
                <OrgPicker
                  value={form.organization_ref ? { ref: form.organization_ref, name: form.organization_name } : null}
                  onChange={(row) => {
                    set("organization_ref", row?.ref ?? "");
                    set("organization_name", row?.name ?? "");
                  }}
                />
              </div>
            )}
            <div className={isInternal ? "w-full" : "w-44"}>
              <div className="mb-1 text-xs font-medium text-muted-foreground">Категория перевозки</div>
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value as StopCategory | "")}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">—</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Название точки — только для ручного адреса (для книги имя = алиас) */}
          {addrMode === "manual" && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">Название точки</div>
              <Input
                value={form.label}
                onChange={(e) => set("label", e.target.value)}
                placeholder="напр. ОЗОН Софьино"
                className="h-9"
              />
            </div>
          )}

          {/* Заказы — герой (только shipment/loading) */}
          {isInternal ? (
            <div className="rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
              <Info className="mr-1 inline size-3.5 align-text-bottom" /> Наша локация: организация и заказы скрыты,
              адрес — из «наших локаций».
            </div>
          ) : (
            <OrdersSection
              tripId={tripId}
              tripDate={tripDate}
              role={form.role}
              organizationRef={form.organization_ref}
              orders={stop.orders}
              onAttach={onAttach}
              onDetach={onDetach}
            />
          )}
        </div>

        {/* Правый сайдбар — Логистика */}
        <div className="shrink-0 space-y-3 rounded-lg border bg-muted/30 p-3 lg:w-60">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Логистика</div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Груз</span>
              {!isInternal && (
                <button
                  type="button"
                  onClick={fillCargoFromOrders}
                  disabled={cargoLoading || stop.orders.length === 0}
                  title={stop.orders.length === 0 ? "Сначала привяжите заказы" : "Посчитать вес и шт из заказов"}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                >
                  {cargoLoading ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />} из заказов
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <UnitField value={form.weight_kg} onChange={(v) => set("weight_kg", v)} unit="кг" />
              <UnitField value={form.pallets} onChange={(v) => set("pallets", v)} unit="плт" />
              <UnitField value={form.items_count} onChange={(v) => set("items_count", v)} unit="шт" />
              <UnitField value={form.boxes} onChange={(v) => set("boxes", v)} unit="кор" />
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Деньги</div>
            <div className="space-y-2">
              <MoneyField label="Дост." value={form.delivery_cost} onChange={(v) => set("delivery_cost", v)} />
              <MoneyField label="Ожид." value={form.waiting_fee} onChange={(v) => set("waiting_fee", v)} />
              {form.status === "returned" && (
                <MoneyField label="Возвр." value={form.return_fee} onChange={(v) => set("return_fee", v)} />
              )}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Статус</div>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value as StopStatus)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {STOP_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Таймслот</div>
            <div className="flex items-center gap-1">
              <Input type="time" value={form.timeslot_from} onChange={(e) => set("timeslot_from", e.target.value)} className="h-9 px-1.5 tabular-nums text-muted-foreground" />
              <span className="text-muted-foreground">—</span>
              <Input type="time" value={form.timeslot_to} onChange={(e) => set("timeslot_to", e.target.value)} className="h-9 px-1.5 tabular-nums text-muted-foreground" />
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Комментарий</div>
            <textarea
              rows={2}
              value={form.comment}
              onChange={(e) => set("comment", e.target.value)}
              placeholder="напр. въезд со двора"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Мелкие поля ───────────────────────────────────────────────────────────

function RowActions({
  index,
  total,
  onMove,
  onDelete,
}: {
  index: number;
  total: number;
  onMove: (index: number, dir: -1 | 1) => void;
  onDelete: () => void;
}): React.ReactElement {
  return (
    <div className="flex shrink-0 items-center text-muted-foreground">
      <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="rounded p-1 hover:bg-accent disabled:opacity-40" aria-label="Выше">
        <ChevronUp className="size-4" />
      </button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="rounded p-1 hover:bg-accent disabled:opacity-40" aria-label="Ниже">
        <ChevronDown className="size-4" />
      </button>
      <button type="button" onClick={onDelete} className="rounded p-1 hover:bg-destructive/10 hover:text-destructive" aria-label="Удалить точку">
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

// Число с единицей-суффиксом внутри поля, без спиннеров.
function UnitField({
  value,
  onChange,
  unit,
}: {
  value: string;
  onChange: (v: string) => void;
  unit: string;
}): React.ReactElement {
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(NUM_INPUT, "pl-2.5 pr-8")}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
        {unit}
      </span>
    </div>
  );
}

// Деньги: префикс-лейбл слева + ₽ справа.
function MoneyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(NUM_INPUT, "pl-14 pr-7")}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₽</span>
    </div>
  );
}

// ── Пикер организации (h-9, постоянная высота) ────────────────────────────

function OrgPicker({
  value,
  onChange,
}: {
  value: { ref: string; name: string } | null;
  onChange: (v: { ref: string; name: string } | null) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const orgsQ = useQuery({
    queryKey: ["logistics-orgs", debounced],
    queryFn: () => listLogisticsOrganizations(debounced),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const rows = orgsQ.data ?? [];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent"
      >
        <span className={cn("min-w-0 truncate", !value && "text-muted-foreground")}>
          {value?.name || "Организация точки…"}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border bg-card shadow-md">
          <div className="border-b p-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск организации…" className="h-8 w-full rounded border border-input px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" autoFocus />
          </div>
          <ul className="max-h-56 overflow-auto py-1">
            {value && (
              <li>
                <button type="button" className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent" onClick={() => { onChange(null); setOpen(false); setQ(""); }}>
                  — не выбрана —
                </button>
              </li>
            )}
            {orgsQ.isFetching && <li className="px-3 py-2 text-sm text-muted-foreground">Загрузка…</li>}
            {!orgsQ.isFetching && rows.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">{debounced ? "Ничего не найдено" : "Пусто"}</li>
            )}
            {rows.map((r) => (
              <li key={r.ref}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                  onClick={() => {
                    onChange({ ref: r.ref, name: r.name });
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Пикер сохранённого адреса (по kind), h-11 ─────────────────────────────

function SavedAddressPicker({
  kind,
  placeholder,
  current,
  onPick,
}: {
  kind: "client" | "own";
  placeholder: string;
  current: string;
  onPick: (address: string, alias: string, lat: string | null, lon: string | null) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const bookQ = useQuery({
    queryKey: ["addresses-book", debounced, kind],
    queryFn: () => listAddresses(debounced, kind),
    enabled: open,
    staleTime: 30_000,
  });
  const rows = bookQ.data ?? [];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm hover:bg-accent"
      >
        <span className="flex min-w-0 items-center gap-2">
          {kind === "own" ? <Home className="size-4 shrink-0 text-muted-foreground" /> : <BookMarked className="size-4 shrink-0 text-muted-foreground" />}
          <span className={cn("truncate", !current && "text-muted-foreground")}>{current || placeholder}</span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border bg-card shadow-md">
          <div className="border-b p-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск в книге адресов…" className="h-8 w-full rounded border border-input px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" autoFocus />
          </div>
          <ul className="max-h-56 overflow-auto py-1">
            {bookQ.isFetching && <li className="px-3 py-2 text-sm text-muted-foreground">Загрузка…</li>}
            {!bookQ.isFetching && rows.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">{debounced ? "Ничего не найдено" : "Пусто"}</li>
            )}
            {rows.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-accent"
                  onClick={() => {
                    onPick(a.address, a.alias, a.lat, a.lon);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  <span className="text-sm font-medium">{a.alias}</span>
                  <span className="truncate text-xs text-muted-foreground">{a.address}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Поле адреса (ручной ввод + DaData), h-11 ──────────────────────────────

function AddressField({
  value,
  onChange,
  onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (address: string, lat: string | null, lon: string | null) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 250);
    return () => clearTimeout(t);
  }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const dadataQ = useQuery({
    queryKey: ["dadata", debounced],
    queryFn: () => addressSuggest(debounced),
    enabled: open && debounced.length >= 3,
    retry: false,
    staleTime: 30_000,
  });
  const dadataErr = dadataQ.isError ? extractErrorMessage(dadataQ.error, "DaData недоступна") : null;
  const dadata = dadataQ.data ?? [];

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Начните вводить адрес — проверим по DaData…"
          className="h-11 w-full rounded-md border border-input bg-background pl-3 pr-9 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <Search className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      </div>
      {open && (dadata.length > 0 || dadataErr || dadataQ.isFetching) && (
        <ul className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-card shadow-md">
          <li className="bg-muted/50 px-3 py-1 text-[10px] uppercase text-muted-foreground">Проверенные адреса (DaData)</li>
          {dadataQ.isFetching && <li className="px-3 py-2 text-sm text-muted-foreground">Проверяем…</li>}
          {dadataErr && <li className="px-3 py-2 text-xs text-amber-600">{dadataErr}</li>}
          {!dadataQ.isFetching && !dadataErr && dadata.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Ничего не найдено</li>
          )}
          {dadata.map((s, idx) => (
            <li key={`dd-${idx}`}>
              <button
                type="button"
                className="flex w-full items-start px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(s.value);
                  onPick(s.value, s.geo_lat, s.geo_lon);
                  setOpen(false);
                }}
              >
                <span className="truncate">{s.value}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Секция заказов (герой) ────────────────────────────────────────────────

function OrdersSection({
  tripId,
  tripDate,
  role,
  organizationRef,
  orders,
  onAttach,
  onDetach,
}: {
  tripId: string;
  tripDate: string | null;
  role: StopRole;
  organizationRef: string;
  orders: StopView["orders"];
  onAttach: (orderType: OrderType, orderRef: string) => void;
  onDetach: (orderId: string) => void;
}): React.ReactElement {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = role === "loading" ? "Заказы поставщику" : "Заказы покупателя";
  const canSearch = !!tripDate && !!organizationRef;

  return (
    <div className="rounded-lg border-2 border-dashed border-primary/20 bg-primary/[0.03] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Package className="size-4 text-muted-foreground" /> {label} <span className="text-muted-foreground">({orders.length})</span>
        </span>
        <button
          type="button"
          disabled={!canSearch}
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="size-3.5" /> Привязать заказ
        </button>
      </div>
      {!canSearch && (
        <p className="mb-2 text-[11px] text-muted-foreground">
          {!tripDate ? "укажите дату рейса — по ней ищем заказы" : "выберите организацию точки"}
        </p>
      )}
      <div className="space-y-1.5">
        {orders.length === 0 && <p className="text-xs text-muted-foreground">Заказы не привязаны.</p>}
        {orders.map((o) => (
          <div key={o.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-2 text-sm">
            <Package className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{o.order_number || "б/н"}</span>
            <span className="truncate text-xs text-muted-foreground">
              · {fmtDate(o.order_date)}
              {o.kontragent_name ? ` · ${o.kontragent_name}` : ""}
            </span>
            {o.amount != null && <span className="ml-auto whitespace-nowrap font-medium tabular-nums">{RUB.format(o.amount)} ₽</span>}
            <button
              type="button"
              onClick={() => onDetach(o.id)}
              className={cn("rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive", o.amount == null && "ml-auto")}
              aria-label="Отвязать"
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
      {pickerOpen && (
        <div className="mt-2">
          <OrderPicker
            tripId={tripId}
            role={role}
            organizationRef={organizationRef}
            onAttach={(t, r) => {
              onAttach(t, r);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function OrderPicker({
  tripId,
  role,
  organizationRef,
  onAttach,
  onClose,
}: {
  tripId: string;
  role: StopRole;
  organizationRef: string;
  onAttach: (orderType: OrderType, orderRef: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const ordersQ = useQuery({
    queryKey: ["order-search", tripId, role, organizationRef, debounced],
    queryFn: () => searchOrders(tripId, { role, organization_ref: organizationRef, q: debounced }),
    retry: false,
    staleTime: 15_000,
  });
  const items = ordersQ.data?.items ?? [];

  return (
    <div className="space-y-2 rounded-md border bg-card p-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Номер заказа" className="h-9 pl-8" autoFocus />
        </div>
        <Button variant="ghost" size="icon" className="size-9" onClick={onClose} aria-label="Закрыть">
          <X className="size-4" />
        </Button>
      </div>
      {ordersQ.isLoading && (
        <div className="flex justify-center py-4 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
      {ordersQ.isError && <p className="px-2 py-2 text-sm text-destructive">{extractErrorMessage(ordersQ.error, "Ошибка поиска")}</p>}
      {!ordersQ.isLoading && !ordersQ.isError && items.length === 0 && (
        <p className="px-2 py-3 text-center text-sm text-muted-foreground">Заказы по этой организации и дате рейса (±1 день) не найдены</p>
      )}
      <ul className="max-h-72 space-y-1 overflow-auto">
        {items.map((e) => (
          <li key={e.ref}>
            <button
              type="button"
              onClick={() => onAttach(e.order_type, e.ref)}
              className="flex w-full items-center gap-2 rounded-md border bg-card px-2.5 py-2 text-left text-sm hover:bg-accent"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{e.number}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(e.date)}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{e.kontragent_name}</div>
              </div>
              <span className="whitespace-nowrap text-xs font-medium tabular-nums text-muted-foreground">{RUB.format(e.amount)} ₽</span>
              <Plus className="size-4 shrink-0 text-muted-foreground" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
